import * as Analytics from 'analytics/Analytics';
import {structuredTableActionOrigin} from 'analytics/Util';
import {assert} from 'Asserts';
import {DataModificationsHeader} from 'audit/DataModifications';
import {ELN_ANALYTICS_ACTIONS} from 'constants/AnalyticsConstants';
import {callSampleStatusWarningModal} from 'inventory/sample-status/SampleStatusWarningModal';
import {ACTION_TRANSFER_ROWS} from 'notebook/store/VersioningConstants';
import makeSubmitRowActionFunction, {
  getRowAwareLoadingMessage,
} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {
  ActionResultType,
  performServerSideTableWriteActionWithAudits,
} from 'notebook/structured-tables/action-functions/PerformActionFunctions';
import {
  doPerformTransfer,
  getSampleIdsForStatusWarningFromStructuredTable,
} from 'notebook/structured-tables/action-functions/transferActionFunction';
import * as ActionFunctionDAO from 'notebook/structured-tables/ActionFunctionDAO';
import invokeStructuredTableMappings from 'notebook/structured-tables/structured-table-mappings/invokeStructuredTableMappings';
import {AxisContextMenuActionFunctionParameters} from 'notebook/structured-tables/StructuredTableConstants';
import {TableSubmissionDiffs} from 'notebook/structured-tables/util/datamods/getStructuredTableCellDiffOnSubmission';
import {confirmIfClearingSampleOwners} from 'notebook/structured-tables/util/getSampleControlRolesFromTable';
import {isInExecutableCanvas} from 'notebook/structured-tables/util/isInExecutableCanvas';
import {TransferTableColumnGroup} from 'notebook/transfer-tables/TransferTableConstants';

import {
  TransferStructuredTable,
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata,
} from './TransferStructuredTableAdapter';

export const SUBMIT_TRANSFER_ROW_ACTION_FUNCTION_CONFIG = {
  allowOutsideOfEntry: false,
  onSelectForSubmit: async (
    actionFunctionParams: AxisContextMenuActionFunctionParameters<
      TransferStructuredTableMetadata,
      TransferStructuredTableRowMetadata
    >
  ): Promise<void> => {
    assert(
      isInExecutableCanvas(actionFunctionParams),
      'Transfer action is only supported in entries and executable documents.',
      {
        apiId: actionFunctionParams.entryOrTemplateId,
      }
    );
    const entryId = actionFunctionParams.entryOrTemplateId;
    assert(entryId != null, 'Document ID not provided');
    const transferStructuredTable: TransferStructuredTable = actionFunctionParams.structuredTable;
    assert(actionFunctionParams.selectionRange != null, 'selectionRange not provided');
    const selectionWithExclusiveEnd = {
      start: actionFunctionParams.selectionRange.minIndex,
      end: actionFunctionParams.selectionRange.maxIndex + 1,
    };

    const slicedStructuredTable = selectionWithExclusiveEnd
      ? transferStructuredTable.rows.slice(selectionWithExclusiveEnd.start, selectionWithExclusiveEnd.end)
      : undefined;
    const slicedComputedTable = selectionWithExclusiveEnd
      ? actionFunctionParams.computedTable.slice(
          selectionWithExclusiveEnd.start,
          selectionWithExclusiveEnd.end
        )
      : actionFunctionParams.computedTable;

    const sampleIds = await getSampleIdsForStatusWarningFromStructuredTable({
      structuredTable: transferStructuredTable,
      slicedStructuredTable,
      computedTable: slicedComputedTable,
    });
    const {shouldProceed: proceedWithSampleOverride} = await callSampleStatusWarningModal({
      sampleIds: [...new Set(sampleIds)],
      trackingParams: {
        actionOrigin: structuredTableActionOrigin(transferStructuredTable),
        actionAttempted: 'transfer',
        eventGroupId: Analytics.makeAnalyticsEventId(),
      },
      canSelectMultiple: false,
      showOnlyWarnings: true,
    });
    const proceedWithSubmit =
      proceedWithSampleOverride &&
      (await confirmIfClearingSampleOwners({
        structuredTable: transferStructuredTable,
        slicedStructuredTable,
        computedTable: slicedComputedTable,
        targetSampleColumnGroup: TransferTableColumnGroup.TARGET,
      }));

    if (proceedWithSubmit) {
      await performServerSideTableWriteActionWithAudits<ActionFunctionDAO.TransferSubmitResponse>({
        entryId,
        loadingScreenMessage: getRowAwareLoadingMessage(
          selectionWithExclusiveEnd,
          'Transferring Rows',
          'Transferring Row'
        ),
        autoSaverAction: ACTION_TRANSFER_ROWS,
        doPerformAction: (
          tableSubmissionDiffs?: TableSubmissionDiffs,
          updateStatus?: (msg: string) => void,
          dataModsHeader?: DataModificationsHeader
        ) =>
          doPerformTransfer(
            entryId,
            transferStructuredTable,
            actionFunctionParams.computedTable,
            actionFunctionParams.actionFunctionContext,
            tableSubmissionDiffs,
            selectionWithExclusiveEnd,
            updateStatus,
            dataModsHeader
          ),
        doPostRefresh: (actionResult) =>
          new Promise((resolve) => {
            invokeStructuredTableMappings(
              entryId,
              transferStructuredTable,
              actionResult.resultType === ActionResultType.PARTIAL_SUCCESS
                ? actionResult.submittedRange
                : selectionWithExclusiveEnd
            );
            resolve();
          }),
        analyticsActionForPerfTracking:
          ELN_ANALYTICS_ACTIONS.TRANSFERRED_ROW_SAMPLES_FROM_STRUCTURED_TABLE.action,
        structuredTables: [actionFunctionParams.structuredTable],
        structuredTableAdapters: [actionFunctionParams.structuredTableAdapter],
        computedTables: [actionFunctionParams.computedTable],
        tables: actionFunctionParams.table ? [actionFunctionParams.table] : [],
        selection: selectionWithExclusiveEnd,
      });
    }
  },
};

export default makeSubmitRowActionFunction<
  TransferStructuredTableMetadata,
  TransferStructuredTableRowMetadata
>(SUBMIT_TRANSFER_ROW_ACTION_FUNCTION_CONFIG);
